import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CodeAddRequestModel,
  CodeAddResultModel,
  CodeParseResultModel,
  CodeType,
  DataApiResponse,
  GiftCardCodeBatchModel,
  GiftCardCodeBatchResultModel,
  GiftCardCodesConfigEditModel,
  GiftCardCodesConfigModel,
  PointsOfSaleProductModel,
  POS,
  Product,
  ProductAllergensModel,
  ProductBasic,
  ProductBoxOfficeConfigModel,
  ProductCombiSlotsModel,
  ProductDisplayStatus,
  ProductGeneralInfoModel,
  ProductGiftcardConfigModel,
  ProductPricesModel,
  ProductProductPricingTagsModel,
  ProductScannerInfoModel,
  ProductStockModel,
  ProductSupplementsModel,
  ProductTagType,
  ProductType,
  ProductVariantsConfigModel,
  RestaurantProductModel,
  SeatTypesProductModel,
  ShopType,
  VoucherStatus,
} from '@digilize/shared/definitions/src';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ProductsApiService {
  constructor(private http: HttpClient) {}

  readonly BASE_URL = '/api/v1.0/products';

  addProduct(product: Product) {
    return this.http.post<Product>(`${this.BASE_URL}`, product);
  }

  editProduct(product: Product) {
    return this.http.put<Product>(`${this.BASE_URL}`, product);
  }

  getProducts(params: GetProductsQuery) {
    return this.http.get<DataApiResponse<Product[]>>(`${this.BASE_URL}`, { params: { ...params } });
  }

  deleteProduct(id: string) {
    return this.http.delete(`${this.BASE_URL}/${id}`);
  }

  cloneProduct(id: string) {
    return this.http.post<Product>(`${this.BASE_URL}/${id}/clone`, {});
  }

  getProduct(id: string) {
    return this.http.get<Product>(`${this.BASE_URL}/${id}`);
  }

  parseVoucherCodes(payload: { id: string; codes: string; type: CodeType }) {
    return this.http.post<CodeParseResultModel>(`${this.BASE_URL}/vouchers/parse`, payload);
  }

  addVoucherCodes(vouchers: CodeAddRequestModel) {
    return this.http.post<CodeAddResultModel>(`${this.BASE_URL}/vouchers`, vouchers);
  }

  activateProduct(id: string) {
    return this.http.post<Product>(`${this.BASE_URL}/${id}/activate`, {});
  }

  deactivateProduct(id: string) {
    return this.http.post<Product>(`${this.BASE_URL}/${id}/deactivate`, {});
  }

  activateProducts(ids: string[]) {
    return this.http.post<CodeAddResultModel>(`${this.BASE_URL}/activate`, { ids });
  }

  deactivateProducts(ids: string[]) {
    return this.http.post<CodeAddResultModel>(`${this.BASE_URL}/deactivate`, { ids });
  }

  getBasicProducts(params): Observable<DataApiResponse<ProductBasic[]>> {
    return this.http.get<DataApiResponse<ProductBasic[]>>(`${this.BASE_URL}/basic`, { params: { ...params } });
  }

  searchProducts(query: string) {
    return this.http.get(`${this.BASE_URL}/search`, { params: { q: query } });
  }

  getHighlightedProducts() {
    return this.http.get(`${this.BASE_URL}/highlighted`);
  }

  getMakers() {
    return this.http.get(`${this.BASE_URL}/makers`);
  }

  getPublicProduct(id: string) {
    return this.http.get(`${this.BASE_URL}/${id}/public`);
  }

  getProductSupplements(
    params: { draw: number; start: number; length: number },
    payload: { products_ids?: string[]; depth: number }
  ) {
    return this.http.post<DataApiResponse<Product[]>>(`${this.BASE_URL}/supplements`, payload, {
      params: { ...params },
    });
  }

  getUpsellProducts(id: string) {
    return this.http.get(`${this.BASE_URL}/${id}/upsell`);
  }

  getProductsByVouchers() {
    return this.http.get(`${this.BASE_URL}/by-vouchers`);
  }

  exportProducts() {
    return this.http.get(`${this.BASE_URL}/export`, { responseType: 'blob' });
  }

  importProducts(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post(`${this.BASE_URL}/import`, formData);
  }

  rollbackImport(id: string) {
    return this.http.post(`${this.BASE_URL}/import/${id}/rollback`, {});
  }

  addRestaurantProduct(payload: RestaurantProductModel) {
    return this.http.post<RestaurantProductModel>(`${this.BASE_URL}/restaurant`, payload);
  }

  syncRestaurantProduct(payload: RestaurantProductModel) {
    return this.http.put<Product>(`${this.BASE_URL}/restaurant`, payload);
  }

  syncProductToSeatType(payload: SeatTypesProductModel) {
    return this.http.put<Product>(`${this.BASE_URL}/seat-types`, payload);
  }

  syncProductGiftcard(payload: ProductGiftcardConfigModel) {
    return this.http.put<Product>(`${this.BASE_URL}/giftcard`, payload);
  }

  // ── GIFT CARD CODES (PAUL-2019) ───────────────────────────────────────────
  // Keyed on the product, so the Codes tab opens on a gift-card product that has never been
  // configured — the config row is created by the first save.

  getGiftCardCodesConfig(productId: string): Observable<GiftCardCodesConfigModel> {
    return this.http.get<GiftCardCodesConfigModel>(`${this.BASE_URL}/${productId}/gift-card-codes/config`);
  }

  syncGiftCardCodesConfig(
    productId: string,
    payload: GiftCardCodesConfigEditModel
  ): Observable<GiftCardCodesConfigModel> {
    return this.http.put<GiftCardCodesConfigModel>(`${this.BASE_URL}/${productId}/gift-card-codes/config`, payload);
  }

  getGiftCardCodeBatches(
    productId: string,
    params?: { draw?: number; start?: number; length?: number }
  ): Observable<DataApiResponse<GiftCardCodeBatchModel[]>> {
    return this.http.get<DataApiResponse<GiftCardCodeBatchModel[]>>(
      `${this.BASE_URL}/${productId}/gift-card-codes/batches`,
      { params: params as Record<string, any> }
    );
  }

  // The prefix is never sent — the product's configured `card_prefix` is applied server-side, so two
  // batches of one product can never carry differently-shaped codes.
  generateGiftCardCodeBatch(productId: string, amount: number): Observable<GiftCardCodeBatchResultModel> {
    const form = new FormData();
    form.append('Mode', '0');
    form.append('Amount', String(amount));
    return this.http.post<GiftCardCodeBatchResultModel>(`${this.BASE_URL}/${productId}/gift-card-codes/batches`, form);
  }

  uploadGiftCardCodeBatch(productId: string, formData: FormData): Observable<GiftCardCodeBatchResultModel> {
    return this.http.post<GiftCardCodeBatchResultModel>(
      `${this.BASE_URL}/${productId}/gift-card-codes/batches`,
      formData
    );
  }

  // Full response — the filename to save under comes from the `Content-Disposition` header, since the
  // export is an xlsx workbook unless `format=csv` is asked for.
  exportGiftCardCodeBatch(productId: string, batchId: string): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.BASE_URL}/${productId}/gift-card-codes/batches/${batchId}/export`, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  deleteGiftCardCodeBatch(productId: string, batchId: string): Observable<void> {
    return this.http.delete<void>(`${this.BASE_URL}/${productId}/gift-card-codes/batches/${batchId}`);
  }

  getRestaurantProducts(params: GetRestaurantProductConfigQuery) {
    return this.http.get<DataApiResponse<RestaurantProductModel[]>>(`${this.BASE_URL}/restaurant`, {
      params: { ...params },
    });
  }

  getRestaurantProductById(id: string) {
    return this.http.get<RestaurantProductModel>(`${`${this.BASE_URL}/restaurant`}/${id}`);
  }

  deleteRestaurantProduct(id: string) {
    return this.http.delete(`${`${this.BASE_URL}/restaurant`}/${id}`);
  }

  getProductPointsOfSale(id: string) {
    return this.http.get<POS[]>(`${this.BASE_URL}/${id}/pos`);
  }

  syncProductsToPointsOfSale(payload: PointsOfSaleProductModel) {
    return this.http.put<Product>(`${this.BASE_URL}/pos`, payload);
  }

  createProduct(payload: ProductGeneralInfoModel) {
    return this.http.put<Product>(`${this.BASE_URL}/general-info`, payload);
  }

  syncProductsPrices(payload: ProductPricesModel) {
    return this.http.put<Product>(`${this.BASE_URL}/prices`, payload);
  }

  syncProductsStock(payload: ProductStockModel) {
    return this.http.put<Product>(`${this.BASE_URL}/stock`, payload);
  }

  syncProductsRequiredSupplements(payload: ProductSupplementsModel) {
    return this.http.put<Product>(`${this.BASE_URL}/required-supplements`, payload);
  }

  syncProductsSupplements(payload: ProductSupplementsModel) {
    return this.http.put<Product>(`${this.BASE_URL}/supplements`, payload);
  }

  syncProductVariants(payload: ProductVariantsConfigModel) {
    return this.http.put<Product>(`${this.BASE_URL}/variants`, payload);
  }

  syncProductCombiSlots(payload: ProductCombiSlotsModel) {
    return this.http.put<Product>(`${this.BASE_URL}/combi-slots`, payload);
  }

  syncProductAllergens(payload: ProductAllergensModel) {
    return this.http.put<Product>(`${this.BASE_URL}/allergens`, payload);
  }

  syncProductsScannerInfo(payload: ProductScannerInfoModel) {
    return this.http.put<Product>(`${this.BASE_URL}/scanner-info`, payload);
  }

  syncProductBoxOffice(payload: ProductBoxOfficeConfigModel) {
    return this.http.put<Product>(`${this.BASE_URL}/box-office`, payload);
  }

  syncProductsPricingTags(payload: ProductProductPricingTagsModel) {
    return this.http.put<Product>(`${this.BASE_URL}/product-pricing-tags`, payload);
  }
}

export interface GetRestaurantProductConfigQuery {
  shop?: string;
  product?: string;
  cart?: string;
  email_statuses?: string[];
  day?: string;
  from?: string;
  to?: string;
  include?: string;
  exclude?: string;
  country?: string;
  company?: string;
  tag_type?: ProductTagType;
  users?: boolean;
  moderators?: boolean;
  language?: string;
  user?: string;
  searchValue?: string;
  searchRegex?: string;
  orderColumnName?: string;
  orderColumnDescending?: boolean;
  draw?: number;
  start?: number;
  length?: number;
}

export interface GetProductsQuery {
  session?: string;
  session_id?: string;
  shop?: string;
  shop_types?: ShopType[];
  group?: string;
  subcategory?: string;
  product_types?: ProductType[];
  statuses?: ProductDisplayStatus[];
  active?: boolean;
  voucher_status?: VoucherStatus;
  user_status?: string;
  feed?: string;
  email_statuses?: string[];
  day?: string;
  from?: string;
  to?: string;
  include?: string;
  exclude?: string;
  country?: string;
  company?: string;
  tag_type?: ProductTagType;
  users?: boolean;
  moderators?: boolean;
  language?: string;
  user?: string;
  searchValue?: string;
  searchRegex?: string;
  orderColumnName?: string;
  orderColumnDescending?: boolean;
  draw?: number;
  start?: number;
  length?: number;
  'search.value'?: string;
  cancellation_fee?: boolean;
}
